import React from 'react';
import { createPortal } from 'react-dom';
import { CircleDot, FolderKanban, SlidersHorizontal, Tags } from 'lucide-react';
import type { EmployeeMultiSelectOption } from '../employees/EmployeeMultiSelect';
import { EmployeeMultiSelect } from '../employees/EmployeeMultiSelect';
import type { FilterMultiSelectOption } from '../FilterMultiSelect';
import { FilterMultiSelect } from '../FilterMultiSelect';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { SCHEDULING_STATUS_FILTER_OPTIONS } from './plantafelTypes';
import { PLANTAFEL_FILTER_TRIGGER_CLASS } from './plantafelToolbarStyles';
import { FILTER_GROUP_LABEL_CLASS } from '../../ui/controlStyles';

const FILTER_PANEL_WIDTH = 300;
const FILTER_GAP_PX = 12;
/** Prevents flip-flopping when expanding would leave almost no spare width. */
const EXPAND_HYSTERESIS_PX = 32;

export type PlantafelToolbarFiltersProps = {
  employees: EmployeeMultiSelectOption[];
  projektOptions: FilterMultiSelectOption[];
  typOptions: FilterMultiSelectOption[];
  mitarbeiter: string[];
  projekt: string[];
  typ: string[];
  status: string[];
  onMitarbeiterChange: (ids: string[]) => void;
  onProjektChange: (ids: string[]) => void;
  onTypChange: (ids: string[]) => void;
  onStatusChange: (ids: string[]) => void;
  onResetFilters: () => void;
  typPanelHint?: string;
  statusPanelHint?: string;
  /** Always-visible controls before the filters (e.g. date range). */
  leading?: React.ReactNode;
  /** Controls that stay visible next to the filters (view mode, create, …). */
  children?: React.ReactNode;
  className?: string;
};

function countActivePlantafelFilters(
  mitarbeiter: string[],
  projekt: string[],
  typ: string[],
  status: string[],
): number {
  return (
    (mitarbeiter.length > 0 ? 1 : 0) +
    (projekt.length > 0 ? 1 : 0) +
    (typ.length > 0 ? 1 : 0) +
    (status.length > 0 ? 1 : 0)
  );
}

function PlantafelFilterFields({
  employees,
  projektOptions,
  typOptions,
  mitarbeiter,
  projekt,
  typ,
  status,
  onMitarbeiterChange,
  onProjektChange,
  onTypChange,
  onStatusChange,
  typPanelHint,
  statusPanelHint,
  stacked = false,
}: {
  employees: EmployeeMultiSelectOption[];
  projektOptions: FilterMultiSelectOption[];
  typOptions: FilterMultiSelectOption[];
  mitarbeiter: string[];
  projekt: string[];
  typ: string[];
  status: string[];
  onMitarbeiterChange: (ids: string[]) => void;
  onProjektChange: (ids: string[]) => void;
  onTypChange: (ids: string[]) => void;
  onStatusChange: (ids: string[]) => void;
  typPanelHint?: string;
  statusPanelHint?: string;
  stacked?: boolean;
}) {
  const triggerClass = stacked
    ? `${PLANTAFEL_FILTER_TRIGGER_CLASS} w-full justify-between`
    : PLANTAFEL_FILTER_TRIGGER_CLASS;
  const wrapClass = stacked ? 'w-full' : undefined;
  const panelAlign = stacked ? 'start' : 'end';

  const mitarbeiterField = (
    <EmployeeMultiSelect
      employees={employees}
      selected={mitarbeiter}
      onChange={onMitarbeiterChange}
      selectionMode="emptyMeansAll"
      showSubtitle
      className={wrapClass}
      triggerClassName={triggerClass}
      panelAlign={panelAlign}
    />
  );
  const projektField = (
    <FilterMultiSelect
      options={projektOptions}
      selected={projekt}
      onChange={onProjektChange}
      selectionMode="emptyMeansAll"
      allLabel="Alle Projekte"
      countLabel="Projekte"
      searchPlaceholder="Projekt suchen"
      emptyListLabel="Keine Projekte"
      showSubtitle
      icon={<FolderKanban className="h-3.5 w-3.5" aria-hidden />}
      className={wrapClass}
      triggerClassName={triggerClass}
      width={320}
      panelAlign={panelAlign}
      ariaLabel="Projekte filtern"
    />
  );
  const typField = (
    <FilterMultiSelect
      options={typOptions}
      selected={typ}
      onChange={onTypChange}
      selectionMode="emptyMeansAll"
      allLabel="Alle Typen"
      countLabel="Typen"
      searchPlaceholder="Typ suchen"
      emptyListLabel="Keine Typen"
      icon={<Tags className="h-3.5 w-3.5" aria-hidden />}
      className={wrapClass}
      triggerClassName={triggerClass}
      width={280}
      panelAlign={panelAlign}
      ariaLabel="Aktivitäts- und Abwesenheitstyp filtern"
      panelHint={typPanelHint}
    />
  );
  const statusField = (
    <FilterMultiSelect
      options={SCHEDULING_STATUS_FILTER_OPTIONS.map((o) => ({ id: o.id, label: o.label }))}
      selected={status}
      onChange={onStatusChange}
      selectionMode="emptyMeansAll"
      allLabel="Alle Status"
      countLabel="Status"
      searchPlaceholder="Status suchen"
      emptyListLabel="Keine Status"
      icon={<CircleDot className="h-3.5 w-3.5" aria-hidden />}
      className={wrapClass}
      triggerClassName={triggerClass}
      width={240}
      panelAlign={panelAlign}
      ariaLabel="Status filtern"
      panelHint={statusPanelHint}
    />
  );

  if (!stacked) {
    return (
      <>
        {mitarbeiterField}
        {projektField}
        {typField}
        {statusField}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <span className={FILTER_GROUP_LABEL_CLASS}>Mitarbeiter</span>
        {mitarbeiterField}
      </div>
      <div className="flex flex-col gap-1.5">
        <span className={FILTER_GROUP_LABEL_CLASS}>Projekte</span>
        {projektField}
      </div>
      <div className="flex flex-col gap-1.5">
        <span className={FILTER_GROUP_LABEL_CLASS}>Typen</span>
        {typField}
      </div>
      <div className="flex flex-col gap-1.5">
        <span className={FILTER_GROUP_LABEL_CLASS}>Status</span>
        {statusField}
      </div>
    </div>
  );
}

function PlantafelFilterPopover({
  activeCount,
  onResetFilters,
  ...fieldProps
}: {
  activeCount: number;
  onResetFilters: () => void;
} & Omit<Parameters<typeof PlantafelFilterFields>[0], 'stacked'>) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  React.useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const left = Math.min(
        Math.max(8, rect.right - FILTER_PANEL_WIDTH),
        window.innerWidth - FILTER_PANEL_WIDTH - 8,
      );
      setPos({ top: rect.bottom + 6, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      // Nested FilterMultiSelect panels are portaled to body.
      if (target.closest?.('[data-filter-multi-select-panel]')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEscapeClose(() => setOpen(false), open);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`${PLANTAFEL_FILTER_TRIGGER_CLASS} ${
          activeCount > 0 ? 'text-pipedrive-blue' : ''
        }`}
        title="Filter"
        aria-label="Plantafel filtern"
      >
        <SlidersHorizontal className="h-4 w-4" strokeWidth={1.75} />
        Filter
        {activeCount > 0 ? (
          <span className="tabular-nums">{activeCount}</span>
        ) : null}
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Plantafel filtern"
              style={{ top: pos.top, left: pos.left, width: FILTER_PANEL_WIDTH }}
              className="fixed z-[80] rounded-lg border border-app-border bg-app-surface p-4 shadow-lg"
            >
              <PlantafelFilterFields {...fieldProps} stacked />

              <div className="mt-4 flex items-center justify-between border-t border-app-border pt-3">
                <button
                  type="button"
                  onClick={onResetFilters}
                  disabled={activeCount === 0}
                  className="text-sm font-medium text-app-text-subtle transition-colors hover:text-app-text disabled:opacity-40"
                >
                  Zurücksetzen
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="inline-flex h-9 items-center rounded-md bg-pipedrive-blue px-3.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                >
                  Fertig
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/**
 * Renders the four Plantafel filter dropdowns inline when they fit on one line
 * next to `children`; otherwise collapses them into a single Filter button
 * (same pattern as Projekte).
 */
export function PlantafelToolbarFilters({
  employees,
  projektOptions,
  typOptions,
  mitarbeiter,
  projekt,
  typ,
  status,
  onMitarbeiterChange,
  onProjektChange,
  onTypChange,
  onStatusChange,
  onResetFilters,
  typPanelHint,
  statusPanelHint,
  leading,
  children,
  className = 'ml-auto',
}: PlantafelToolbarFiltersProps) {
  const rowRef = React.useRef<HTMLDivElement>(null);
  const leadingRef = React.useRef<HTMLDivElement>(null);
  const actionsRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const compactRef = React.useRef(false);
  const [compact, setCompact] = React.useState(false);

  const fieldProps = {
    employees,
    projektOptions,
    typOptions,
    mitarbeiter,
    projekt,
    typ,
    status,
    onMitarbeiterChange,
    onProjektChange,
    onTypChange,
    onStatusChange,
    typPanelHint,
    statusPanelHint,
  };

  const activeCount = countActivePlantafelFilters(mitarbeiter, projekt, typ, status);

  React.useLayoutEffect(() => {
    const row = rowRef.current;
    const actions = actionsRef.current;
    const measure = measureRef.current;
    if (!row || !actions || !measure) return;

    const update = () => {
      const leadingWidth = leadingRef.current?.offsetWidth ?? 0;
      const leadingGap = leadingWidth > 0 ? FILTER_GAP_PX : 0;
      // Row uses flex-1 so clientWidth is the leftover toolbar space, not just content width.
      const available =
        row.clientWidth - leadingWidth - leadingGap - actions.offsetWidth - FILTER_GAP_PX;
      const needed = measure.scrollWidth;
      const next = compactRef.current
        ? needed > available - EXPAND_HYSTERESIS_PX
        : needed > available;
      if (next === compactRef.current) return;
      compactRef.current = next;
      setCompact(next);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(row);
    ro.observe(actions);
    ro.observe(measure);
    if (leadingRef.current) ro.observe(leadingRef.current);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [leading != null, mitarbeiter, projekt, typ, status, employees.length, projektOptions.length, typOptions.length]);

  return (
    <div
      ref={rowRef}
      className={`flex min-w-0 flex-1 flex-nowrap items-center justify-end gap-3 ${className}`.trim()}
    >
      {/* Intrinsic width probe — always mounted, never affects layout. */}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute flex w-max items-center gap-3"
        style={{ left: 0, top: 0 }}
      >
        <PlantafelFilterFields {...fieldProps} />
      </div>

      {leading ? (
        <div ref={leadingRef} className="flex shrink-0 flex-nowrap items-center gap-3">
          {leading}
        </div>
      ) : null}

      {compact ? (
        <PlantafelFilterPopover
          {...fieldProps}
          activeCount={activeCount}
          onResetFilters={onResetFilters}
        />
      ) : (
        <div className="flex shrink-0 items-center gap-1">
          <PlantafelFilterFields {...fieldProps} />
        </div>
      )}

      <div ref={actionsRef} className="flex shrink-0 flex-nowrap items-center gap-3">
        {children}
      </div>
    </div>
  );
}
